import { useMemo, useState } from 'react';

import { GlobalLoaderContext } from './GlobalLoaderContext';

import type { PropsWithChildren, ReactNode } from 'react';
import type { GlobalLoaderStateParams } from './GlobalLoaderContext';

export type CommonLoaderComponentProps<
  LoaderComponentProps extends Record<string, unknown> = Record<string, unknown>,
> = {
  open?: boolean;
} & LoaderComponentProps;

type GlobalLoaderProviderProps = {
  /**
   * openLoader 호출할 때 LoaderComponent를 주입하지 않아도 기본으로 표시할 초기 컴포넌트를 설정합니다.
   * 해당 컴포넌트의 props는 CommonLoaderComponentProps 구조를 따릅니다.
   * CommonLoaderComponentProps = { open?: boolean; } & ...
   */
  InitialLoaderComponent?: (props: CommonLoaderComponentProps) => ReactNode;
};

const GlobalLoaderProvider = (props: PropsWithChildren<GlobalLoaderProviderProps>) => {
  const { children, InitialLoaderComponent = () => <></> } = props;
  const [openedLoader, setOpenedLoader] = useState<GlobalLoaderStateParams>({});

  const { Component = InitialLoaderComponent, props: loaderProps = { open: false } } = openedLoader;

  const openLoader = (targetLoader?: GlobalLoaderStateParams) => {
    const { Component = InitialLoaderComponent, props = {} } = targetLoader || {};
    setOpenedLoader({ Component, props: { ...props, open: true } });
  };

  const closeLoader = () => {
    setOpenedLoader(prevState => ({ ...prevState, props: { ...prevState.props, open: false } }));
  };

  const dispatch = useMemo(() => ({ openLoader, closeLoader }), []);

  return (
    <GlobalLoaderContext.Provider value={dispatch}>
      <Component {...loaderProps} />
      {children}
    </GlobalLoaderContext.Provider>
  );
};

export default GlobalLoaderProvider;
